import { useState } from "react";
import { Mail, MessageCircle, X, Send, Cake, Heart, ShoppingBag, Calendar, Gift } from "lucide-react";

type Plantilla = {
  id: string;
  titulo: string;
  icon: React.ReactNode;
  emailAsunto?: string;
  texto: (nombre: string) => string;
};

const PLANTILLAS: Plantilla[] = [
  {
    id: "cumpleanos",
    titulo: "Feliz cumpleaños",
    icon: <Cake className="size-4" />,
    emailAsunto: "¡Feliz cumpleaños desde Robusta! 🎉",
    texto: (n) => `¡Hola ${n}! 🎂\n\nDesde todo el equipo queremos desearte un muy feliz cumpleaños. Para celebrarlo contigo, te invitamos a venir cuando quieras y disfrutar de un postre cortesía de la casa.\n\n¡Te esperamos!`,
  },
  {
    id: "te-extranamos",
    titulo: "Te extrañamos",
    icon: <Heart className="size-4" />,
    emailAsunto: "Te extrañamos en Robusta ❤️",
    texto: (n) => `¡Hola ${n}!\n\nHace tiempo que no te vemos por aquí y queríamos saludarte. Pasa cuando quieras, tenemos novedades en la carta que sabemos te van a gustar.\n\n¡Esperamos verte pronto!`,
  },
  {
    id: "promocion",
    titulo: "Promoción especial",
    icon: <Gift className="size-4" />,
    emailAsunto: "Una promoción pensada para ti 🎁",
    texto: (n) => `¡Hola ${n}!\n\nTenemos una promoción especial que sabemos te va a interesar. Pasa esta semana y disfruta de un descuento exclusivo para clientes frecuentes.\n\n¡Te esperamos!`,
  },
  {
    id: "reserva",
    titulo: "Recordatorio de reserva",
    icon: <Calendar className="size-4" />,
    emailAsunto: "Recordatorio de tu reserva",
    texto: (n) => `¡Hola ${n}!\n\nTe recordamos tu reserva en Robusta. Te esperamos con todo listo para que disfrutes tu visita.\n\nSi necesitas modificar la reserva, contáctanos.`,
  },
  {
    id: "agradecimiento",
    titulo: "Agradecimiento",
    icon: <ShoppingBag className="size-4" />,
    emailAsunto: "Gracias por tu visita",
    texto: (n) => `¡Hola ${n}!\n\nGracias por tu reciente visita a Robusta. Esperamos que hayas disfrutado.\n\nNos encantará verte de vuelta pronto. Si quieres compartir cómo fue tu experiencia, nos ayudaría mucho.`,
  },
];

function limpiarTelefono(tel: string): string {
  // Eliminar todo lo que no sea dígito (excepto +)
  const t = tel.replace(/[^0-9+]/g, "");
  // Si empieza con 0 (típico Venezuela: 0412...), reemplazar por +58
  if (t.startsWith("0") && t.length >= 11) return "+58" + t.substring(1);
  // Si ya empieza con +, dejar
  if (t.startsWith("+")) return t;
  // Si tiene 10+ dígitos y no tiene prefijo, asumir VE
  if (t.length >= 10) return "+58" + t;
  return t;
}

export function ContactarCliente({
  cliente,
  open,
  onClose,
  defaultTab = "email",
}: {
  cliente: { nombre: string | null; email?: string | null; mobil?: string | null; telefono1?: string | null };
  open: boolean;
  onClose: () => void;
  defaultTab?: "email" | "whatsapp";
}) {
  const [tab, setTab] = useState<"email" | "whatsapp">(defaultTab);
  const [plantillaSel, setPlantillaSel] = useState<string | null>("cumpleanos");
  const [texto, setTexto] = useState(() => PLANTILLAS[0].texto(cliente.nombre || "amigo"));
  const [asunto, setAsunto] = useState(PLANTILLAS[0].emailAsunto || "");

  if (!open) return null;

  const tel = cliente.mobil || cliente.telefono1 || "";
  const telLimpio = tel ? limpiarTelefono(tel) : "";

  function aplicarPlantilla(id: string) {
    const p = PLANTILLAS.find((x) => x.id === id);
    if (!p) return;
    setPlantillaSel(id);
    setTexto(p.texto(cliente.nombre || "amigo"));
    if (p.emailAsunto) setAsunto(p.emailAsunto);
  }

  function enviar() {
    if (tab === "email") {
      const url = `mailto:${encodeURIComponent(cliente.email || "")}` +
        `?subject=${encodeURIComponent(asunto)}` +
        `&body=${encodeURIComponent(texto)}`;
      window.location.href = url;
    } else {
      const numero = telLimpio.replace(/[^0-9]/g, ""); // wa.me solo acepta dígitos
      const url = `https://wa.me/${numero}?text=${encodeURIComponent(texto)}`;
      window.open(url, "_blank");
    }
    onClose();
  }

  const canEmail = !!cliente.email;
  const canWhatsapp = !!telLimpio;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in"
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-ink-200">
          <div>
            <h2 className="font-display font-bold text-ink-800">Contactar a {cliente.nombre || "cliente"}</h2>
            <p className="text-xs text-ink-500">Elige una plantilla o personaliza el mensaje</p>
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700 transition-colors">
            <X className="size-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-ink-200">
          <button
            disabled={!canEmail}
            onClick={() => setTab("email")}
            className={`flex-1 px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2 transition-colors
              ${tab === "email" ? "border-b-2 border-brand-600 text-brand-600"
                : !canEmail ? "text-ink-300 cursor-not-allowed"
                : "text-ink-600 hover:text-ink-800"}`}>
            <Mail className="size-4" />
            Email {!canEmail && "(no disponible)"}
          </button>
          <button
            disabled={!canWhatsapp}
            onClick={() => setTab("whatsapp")}
            className={`flex-1 px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2 transition-colors
              ${tab === "whatsapp" ? "border-b-2 border-success text-success"
                : !canWhatsapp ? "text-ink-300 cursor-not-allowed"
                : "text-ink-600 hover:text-ink-800"}`}>
            <MessageCircle className="size-4" />
            WhatsApp {!canWhatsapp && "(no disponible)"}
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {/* Destinatario */}
          <div className="text-xs text-ink-500">
            {tab === "email"
              ? <>Para: <span className="font-mono text-ink-800">{cliente.email}</span></>
              : <>WhatsApp: <span className="font-mono text-ink-800">{telLimpio}</span></>
            }
          </div>

          {/* Plantillas */}
          <div>
            <label className="block text-xs font-medium text-ink-600 mb-2">Plantilla</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {PLANTILLAS.map((p) => (
                <button key={p.id} onClick={() => aplicarPlantilla(p.id)}
                  className={`px-3 py-2 rounded-lg border text-xs font-medium flex items-center gap-1.5 transition-all
                    ${plantillaSel === p.id
                      ? "border-brand-500 bg-brand-50 text-brand-700"
                      : "border-ink-200 hover:border-brand-300 text-ink-700"}`}>
                  {p.icon}{p.titulo}
                </button>
              ))}
            </div>
          </div>

          {/* Asunto (solo email) */}
          {tab === "email" && (
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1">Asunto</label>
              <input type="text" value={asunto} onChange={(e) => setAsunto(e.target.value)}
                className="input w-full" placeholder="Asunto del email" />
            </div>
          )}

          {/* Mensaje */}
          <div>
            <label className="block text-xs font-medium text-ink-600 mb-1">Mensaje</label>
            <textarea value={texto} onChange={(e) => setTexto(e.target.value)}
              rows={9} className="input w-full font-sans"
              placeholder="Escribe tu mensaje..." />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-ink-200 flex items-center justify-between gap-3 bg-ink-50">
          <p className="text-xs text-ink-500">
            {tab === "email"
              ? "Abrirá tu cliente de correo predeterminado"
              : "Abrirá WhatsApp Web o la app si está instalada"}
          </p>
          <button onClick={enviar}
            disabled={tab === "email" ? !canEmail : !canWhatsapp}
            className={`px-4 py-2 rounded-lg text-white text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50
              ${tab === "email" ? "bg-brand-600 hover:bg-brand-700" : "bg-success hover:bg-emerald-700"}`}>
            <Send className="size-4" />
            {tab === "email" ? "Componer email" : "Abrir WhatsApp"}
          </button>
        </div>
      </div>
    </div>
  );
}
